/**
 * PublicShareCardCTAs - auth-state-aware CTAs on a public preference card share page.
 *
 * - Logged in as host (provider) → "Add this client to my directory"
 * - Logged in as consumer → "Save this look to my /me"
 * - Not logged in → 3 buttons: Sign in / Join the waitlist / I'm a pro - apply to host
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Plus, Bookmark, ArrowRight, LogIn, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicShareCardCTAs({ card, category, categoryName, CategoryIcon }) {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const t = localStorage.getItem("session_token");
        if (!t) { setLoading(false); return; }
        const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
        setMe(r.data);
      } catch { /* not logged in */ }
      finally { setLoading(false); }
    })();
  }, []);

  const isHost = me?.role === "host" || me?.is_pilot || (me?.is_demo && me?.role !== "user");
  const isConsumer = me && !isHost;

  const addToDirectory = async () => {
    if (!card?.user_id) { toast.error("Missing client info"); return; }
    setActionLoading(true);
    try {
      const t = localStorage.getItem("session_token");
      await axios.post(`${API}/provider/clients/from-share-card`, {
        share_id: card.share_id || card.card_id,
        client_user_id: card.user_id,
      }, { headers: { Authorization: `Bearer ${t}` } });
      toast.success("Client added to your directory!");
    } catch (e) {
      toast.error("Could not add: " + (e.response?.data?.detail || e.message));
    } finally { setActionLoading(false); }
  };

  const saveToMe = async () => {
    if (!card?.share_id && !card?.card_id) return;
    setActionLoading(true);
    try {
      const t = localStorage.getItem("session_token");
      await axios.post(`${API}/users/me/saved-cards`, {
        card_id: card.card_id || card.share_id,
      }, { headers: { Authorization: `Bearer ${t}` } });
      toast.success("Saved to your /me");
    } catch (e) {
      toast.error("Could not save: " + (e.response?.data?.detail || e.message));
    } finally { setActionLoading(false); }
  };

  if (loading) return null;

  // ---- LOGGED IN AS HOST ----
  if (isHost) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white" data-testid="public-cta-host">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <CategoryIcon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">Add this client to your directory</h3>
            <p className="text-sm text-gray-300 mb-4">
              Save {card?.user_name || "this client"}'s preferences and inspo to your roster. They'll show up in your kanban so you can send them a booking link.
            </p>
            <Button
              onClick={addToDirectory}
              disabled={actionLoading}
              className="bg-white text-gray-900 hover:bg-gray-100 rounded-full"
              data-testid="add-to-directory-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add to my directory
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---- LOGGED IN AS CONSUMER ----
  if (isConsumer) {
    return (
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white" data-testid="public-cta-consumer">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <Bookmark className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">Love this look?</h3>
            <p className="text-sm text-gray-300 mb-4">
              Save it to your /me as inspo. Reuse it next time you plan a {categoryName.toLowerCase()} service.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={saveToMe} disabled={actionLoading} className="bg-white text-gray-900 hover:bg-gray-100 rounded-full" data-testid="save-to-me-btn">
                <Bookmark className="w-4 h-4 mr-1.5" /> Save to my /me
              </Button>
              <Link to="/me" className="inline-flex items-center px-4 py-2 bg-white/10 text-white rounded-full text-sm hover:bg-white/20">
                Plan my own {categoryName.toLowerCase()} service →
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- NOT LOGGED IN ----
  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white" data-testid="public-cta-anon">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
          <CategoryIcon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-1">
            {card?.share_copy?.provider_cta || `Like this? Get one for yourself.`}
          </h3>
          <p className="text-sm text-gray-300">
            {card?.share_copy?.subtext || `Phygital lets clients share their ${categoryName.toLowerCase()} preferences, inspo, and requirements before appointments.`}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
        <Link to="/login" className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white text-gray-900 rounded-full font-medium hover:bg-gray-100 text-sm" data-testid="public-cta-signin">
          <LogIn className="w-3.5 h-3.5" /> Sign in
        </Link>
        <Link to={`/join?category=${category}`} className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-white/10 text-white border border-white/20 rounded-full font-medium hover:bg-white/20 text-sm" data-testid="public-cta-join">
          <Sparkles className="w-3.5 h-3.5" /> Join Phygital
        </Link>
      </div>
    </div>
  );
}
